import { MetricValue, TrendIndicator } from '@delivery-metrics/shared';
import {
  InfoOutlined,
  TrendingDown,
  TrendingFlat,
  TrendingUp,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';

export interface MetricCardProps {
  title: string;
  value: string | number;
  customDisplay?: string; // Custom formatted display value
  unit?: string;
  trend?: TrendIndicator | 'up' | 'down' | 'stable';
  previousValue?: number;
  changePercentage?: number;
  description?: string;
  subtitle?: string;
  tooltip?: string;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  size?: 'small' | 'medium' | 'large';
  onClick?: () => void;
  loading?: boolean;
  format?: 'currency' | 'percentage' | 'number' | 'decimal';
}

const getTrendIcon = (trend: TrendIndicator | 'up' | 'down' | 'stable') => {
  switch (trend) {
    case 'up':
      return <TrendingUp sx={{ fontSize: 16 }} />;
    case 'down':
      return <TrendingDown sx={{ fontSize: 16 }} />;
    case 'stable':
      return <TrendingFlat sx={{ fontSize: 16 }} />;
    default:
      return null;
  }
};

const getTrendColor = (trend: TrendIndicator | 'up' | 'down' | 'stable') => {
  switch (trend) {
    case 'up':
      return 'success.main';
    case 'down':
      return 'error.main';
    case 'stable':
      return 'text.secondary';
    default:
      return 'text.secondary';
  }
};

const getSizeStyles = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return {
        padding: 2,
        valueSize: 'h6',
        titleSize: 'body2',
      };
    case 'large':
      return {
        padding: 4,
        valueSize: 'h3',
        titleSize: 'h6',
      };
    default:
      return {
        padding: 3,
        valueSize: 'h4',
        titleSize: 'h6',
      };
  }
};

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  customDisplay,
  unit,
  trend,
  previousValue,
  changePercentage,
  description,
  subtitle,
  tooltip,
  color = 'primary',
  size = 'medium',
  onClick,
  loading = false,
  format,
}) => {
  const sizeStyles = getSizeStyles(size);
  const isClickable = Boolean(onClick);

  const formatValue = (val: string | number) => {
    if (typeof val === 'number') {
      if (format === 'currency') {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(val);
      }
      if (format === 'percentage') {
        return `${val.toFixed(1)}%`;
      }
      if (format === 'decimal') {
        return val.toFixed(2);
      }
      // Format large numbers with appropriate suffixes
      if (val >= 1000000) {
        return `${(val / 1000000).toFixed(1)}M`;
      }
      if (val >= 1000) {
        return `${(val / 1000).toFixed(1)}K`;
      }
      return val.toLocaleString();
    }
    return val;
  };

  const renderTrendIndicator = () => {
    if (!trend && changePercentage === undefined) return null;

    const trendToUse =
      trend ||
      (changePercentage! > 0
        ? 'up'
        : changePercentage! < 0
          ? 'down'
          : 'stable');
    const percentage =
      changePercentage !== undefined ? Math.abs(changePercentage) : 0;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: getTrendColor(trendToUse),
          mt: 1,
        }}
      >
        {getTrendIcon(trendToUse)}
        {changePercentage !== undefined && (
          <Typography variant='caption' sx={{ color: 'inherit' }}>
            {percentage.toFixed(1)}%
          </Typography>
        )}
      </Box>
    );
  };

  const cardContent = (
    <CardContent
      sx={{ p: sizeStyles.padding, '&:last-child': { pb: sizeStyles.padding } }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 1,
        }}
      >
        <Typography
          variant={sizeStyles.titleSize as any}
          component='h3'
          color='text.secondary'
          sx={{ fontWeight: 500 }}
        >
          {title}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow>
            <IconButton size='small' sx={{ p: 0.5 }}>
              <InfoOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
        <Typography
          variant={sizeStyles.valueSize as any}
          component='div'
          color={`${color}.main`}
          sx={{ fontWeight: 600, lineHeight: 1.2 }}
        >
          {loading ? '...' : customDisplay || formatValue(value)}
        </Typography>
        {unit && !format && !customDisplay && (
          <Typography variant='body2' color='text.secondary'>
            {unit}
          </Typography>
        )}
      </Box>

      {renderTrendIndicator()}

      {(description || subtitle) && (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ mt: 1, display: 'block' }}
        >
          {description || subtitle}
        </Typography>
      )}

      {previousValue !== undefined && (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ mt: 0.5, display: 'block' }}
        >
          Previous: {formatValue(previousValue)}
        </Typography>
      )}
    </CardContent>
  );

  return (
    <Card
      sx={{
        height: '100%',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': isClickable
          ? {
              transform: 'translateY(-2px)',
              boxShadow: theme => theme.shadows[4],
            }
          : {},
      }}
      onClick={onClick}
    >
      {cardContent}
    </Card>
  );
};

// Helper component for creating metric cards from MetricValue objects
export interface MetricValueCardProps extends Omit<
  MetricCardProps,
  'value' | 'trend' | 'previousValue' | 'changePercentage'
> {
  metric: MetricValue;
}

export const MetricValueCard: React.FC<MetricValueCardProps> = ({
  metric,
  ...props
}) => {
  return (
    <MetricCard
      value={metric.value}
      trend={metric.trend}
      previousValue={metric.previousValue}
      changePercentage={metric.changePercentage}
      unit={metric.unit}
      {...props}
    />
  );
};
